import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IAppDB } from '@/shared/lib/db';
import type { IHttpClient } from '@/shared/contracts/http-client.contract';
import { HttpMethod } from '@/shared/contracts/http-client.contract';

import type { Addon, CartItem } from '@/shared/lib/db/schemas/cart.schema';
import type { GetStoreItemsResponse, StoreItem } from '@/modules/stores/types/get-store-items';
import { ENDPOINT } from '@/modules/stores/types/get-store-items';

import { UIError } from '@/shared/utils/ui-error';

import type {
  CartItemAvailability,
  CheckCartAvailabilityRequest,
  CheckCartAvailabilityResponse,
  UnavailableAddon,
} from '../types/check-cart-availability';

export type UseCaseInput = CheckCartAvailabilityRequest;
export type UseCaseOutput = CheckCartAvailabilityResponse;

/**
 * Verificação de disponibilidade do carrinho (issue 329).
 *
 * Reconcilia os itens e complementos persistidos no carrinho local contra o
 * cardápio atual da loja (endpoint de itens do App). Um produto é considerado
 * indisponível quando não consta mais no cardápio; um complemento é considerado
 * indisponível quando não consta mais entre os complementos do produto.
 *
 * É somente-leitura: apenas relata o estado, sem alterar o carrinho (a decisão
 * de remover/ajustar itens é da camada de UI). Falha de forma segura
 * (fail-closed): na dúvida — item sem `item_id` ou ausente do cardápio — o item
 * é marcado como indisponível, evitando confirmar um pedido com itens que a loja
 * não pode mais atender (OWASP A04 — defaults seguros).
 */
export class CheckCartAvailabilityUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private db: IAppDB;
  private client: IHttpClient;

  constructor(db: IAppDB, client: IHttpClient) {
    this.db = db;
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const storeSlug = input?.storeSlug;

    if (!storeSlug) {
      throw new UIError('STORE_REQUIRED', 'Loja é obrigatória para verificar a disponibilidade.');
    }

    const cartItems = await this.db.items.toArray();

    // Carrinho vazio: nada a verificar — disponível por vacuidade.
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      return { isAvailable: true, items: [], unavailableItems: [] };
    }

    const menuByItemId = await this.loadMenuByItemId(storeSlug);

    const items = cartItems.map(cartItem => this.evaluateItem(cartItem, menuByItemId));
    const unavailableItems = items.filter(item => !item.isAvailable);

    return {
      isAvailable: unavailableItems.length === 0,
      items,
      unavailableItems,
    };
  }

  // Busca o cardápio atual da loja e indexa os produtos por id para
  // reconciliação O(1) (mesma origem/endpoint usados no fluxo de refazer pedido).
  private async loadMenuByItemId(storeSlug: string): Promise<Map<string, StoreItem>> {
    const response = await this.client.sendRequest<GetStoreItemsResponse, never>({
      method: HttpMethod.GET,
      endpoint: `${ENDPOINT}/${storeSlug}/items`,
    });

    const menuByItemId = new Map<string, StoreItem>();

    for (const categoryItems of Object.values(response?.items ?? {})) {
      if (!Array.isArray(categoryItems)) continue;

      for (const storeItem of categoryItems) {
        if (storeItem?.id) menuByItemId.set(storeItem.id, storeItem);
      }
    }

    return menuByItemId;
  }

  private evaluateItem(
    cartItem: CartItem,
    menuByItemId: Map<string, StoreItem>
  ): CartItemAvailability {
    const menuItem = cartItem.item_id ? menuByItemId.get(cartItem.item_id) : undefined;
    const isItemUnavailable = !menuItem;

    const unavailableAddons = menuItem
      ? this.findUnavailableAddons(cartItem.addons ?? [], menuItem)
      : [];

    return {
      cartItemId: cartItem.id,
      itemId: cartItem.item_id,
      name: cartItem.name,
      isItemUnavailable,
      unavailableAddons,
      isAvailable: !isItemUnavailable && unavailableAddons.length === 0,
    };
  }

  // Complementos do carrinho que não constam mais entre os complementos do produto.
  private findUnavailableAddons(addons: Addon[], menuItem: StoreItem): UnavailableAddon[] {
    const availableAddonIds = this.collectAddonIds(menuItem);

    return addons
      .filter(addon => addon?.id && !availableAddonIds.has(addon.id))
      .map(addon => ({ id: addon.id, name: addon.name }));
  }

  private collectAddonIds(menuItem: StoreItem): Set<string> {
    const ids = new Set<string>();

    for (const category of menuItem.addon_categories ?? []) {
      for (const addon of category.addons ?? []) {
        if (addon?.id) ids.add(addon.id);
      }
    }

    return ids;
  }
}
